// src/LedgerLockPage.js
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './LedgerLockPage.module.css';
import ParticleNetwork from './components/ParticleNetwork';
import Seo from './components/Seo';

/* ─── Data ─── */
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.ledger_lock.ledger_lock&pli=1';

const TRUST_CHIPS = [
  'Offline-first',
  'Local data storage',
  'PIN & biometric lock',
  'GST · VAT · Sales Tax',
  'Free plan available',
];

const PRIVACY = [
  { icon: '💾', title: 'Local data storage', body: 'Your accounting data lives on your device by default.' },
  { icon: '🔢', title: 'PIN protection', body: 'Lock the app behind a PIN so only you can open your books.' },
  { icon: '🧬', title: 'Biometric authentication', body: 'Fingerprint or face unlock on supported devices.' },
  { icon: '🛡️', title: 'Secure local backups', body: 'Back up your data safely and restore it whenever you need.' },
  { icon: '📄', title: 'PDF & data export', body: 'Your data stays portable — export it any time.' },
  { icon: '📴', title: 'Full offline functionality', body: 'Every core feature works with no connection at all.' },
];

const MODULES = [
  {
    icon: '🧾',
    title: 'Sales & Invoices',
    items: [
      'Create professional invoices',
      'Track sales and payments',
      'Paid, unpaid and overdue status',
      'Record partial payments',
      'Generate PDF invoices',
      'Share or print invoices',
    ],
  },
  {
    icon: '🤝',
    title: 'Customers & Suppliers',
    items: [
      'Manage customer information',
      'Track customer transactions',
      'Monitor outstanding balances',
      'Manage suppliers and purchases',
      'Track supplier payments',
    ],
  },
  {
    icon: '📦',
    title: 'Products & Inventory',
    items: [
      'Manage products and services',
      'Set prices and taxes',
      'Track stock',
      'Use SKUs and barcodes',
    ],
  },
  {
    icon: '💸',
    title: 'Expenses',
    items: [
      'Record business expenses',
      'Categorize expenses',
      'Track payment methods',
      'Complete expense history',
    ],
  },
];

const TAXES = ['GST', 'VAT', 'Sales Tax', 'Tax-free transactions', 'Custom tax rates'];

const REPORTS = [
  'Sales',
  'Expenses',
  'Profit & Loss',
  'Customer receivables',
  'Supplier payables',
  'Tax summaries',
  'Transaction history',
  'Inventory information',
];

const FLOW = ['Sell', 'Invoice', 'Receive', 'Spend', 'Track', 'Report'];

/* Comparison: LedgerLock vs the typical alternatives (generic categories, no brand claims) */
const COMPARE_ROWS = [
  { feature: 'Works fully offline', ledger: true, cloud: false, sheet: true },
  { feature: 'Data stored on your device by default', ledger: true, cloud: false, sheet: true },
  { feature: 'PIN & biometric protection', ledger: true, cloud: 'partial', sheet: false },
  { feature: 'Professional PDF invoices', ledger: true, cloud: true, sheet: false },
  { feature: 'Partial payments & overdue tracking', ledger: true, cloud: true, sheet: false },
  { feature: 'GST / VAT / Sales Tax built in', ledger: true, cloud: 'partial', sheet: false },
  { feature: 'Inventory with SKUs & barcodes', ledger: true, cloud: 'partial', sheet: false },
  { feature: 'Ready-made business reports', ledger: true, cloud: true, sheet: false },
  { feature: 'Free plan with core accounting', ledger: true, cloud: 'partial', sheet: true },
  { feature: 'Local data stays accessible if you stop paying', ledger: true, cloud: false, sheet: true },
  { feature: 'Optional cloud sync & multi-device', ledger: true, cloud: true, sheet: 'partial' },
];

const WHY_BETTER = [
  {
    num: '01',
    title: 'Your business never waits for Wi-Fi',
    body: 'Most accounting tools are built cloud-first, so no signal means no invoices. LedgerLock is offline-first by design — create invoices, record expenses and review transactions anywhere.',
  },
  {
    num: '02',
    title: 'Your data stays yours',
    body: 'Data is stored locally by default and protected with a PIN and biometrics. Cloud storage is an upgrade you choose, not a requirement to get started.',
  },
  {
    num: '03',
    title: 'No lock-out if a plan ends',
    body: 'Your local accounting data remains accessible even if your Pro trial or subscription ends. Cloud features simply need an eligible Pro plan.',
  },
  {
    num: '04',
    title: 'Simple, not bloated',
    body: 'Built around what owners actually do — sell, invoice, receive, spend, track, report — without the enterprise complexity you never use.',
  },
  {
    num: '05',
    title: 'Built for businesses worldwide',
    body: 'Configurable GST, VAT, sales tax, tax-free transactions and custom rates, so it fits shops, freelancers and traders in different markets.',
  },
  {
    num: '06',
    title: 'Start free, grow when ready',
    body: 'A genuinely useful Free plan, plus a Pro plan with cloud sync and multi-device access. New users may receive a 14-day Pro trial.',
  },
];

const FREE_FEATURES = [
  'Core accounting',
  'Offline access',
  'Local data storage',
  'Customers and products',
  'Invoices',
  'Expense tracking',
  'Basic reports',
  'GST/VAT features',
  'Local backup and restore',
];

const PRO_FEATURES = [
  'Cloud data storage',
  'Secure cloud backup',
  'Data synchronization',
  'Multi-device access',
  'Advanced reports',
  'Expanded business features',
];

const AUDIENCE = ['Small businesses', 'Freelancers', 'Professionals', 'Entrepreneurs', 'Shops', 'Service businesses', 'Trading businesses'];

/* ─── Small helpers ─── */
const Cell = ({ value }) => {
  if (value === true) return <span className={styles.yes} aria-label="Yes">✓</span>;
  if (value === 'partial') return <span className={styles.partial} aria-label="Varies">~</span>;
  return <span className={styles.no} aria-label="No">✕</span>;
};

const PhoneMock = () => (
  <div className={styles.phone} aria-hidden="true">
    <div className={styles.phoneTop}>
      <span className={styles.phoneTitle}>LedgerLock</span>
      <span className={styles.offlinePill}><i /> Offline</span>
    </div>
    <div className={styles.phoneCard}>
      <small>Outstanding receivables</small>
      <strong>₹ 48,250</strong>
      <div className={styles.bar}><span style={{ width: '68%' }} /></div>
    </div>
    <div className={styles.phoneRow}>
      <div><b>INV-0142</b><small>Sharma Traders</small></div>
      <span className={styles.tagPaid}>Paid</span>
    </div>
    <div className={styles.phoneRow}>
      <div><b>INV-0143</b><small>Das &amp; Sons</small></div>
      <span className={styles.tagPart}>Partial</span>
    </div>
    <div className={styles.phoneRow}>
      <div><b>INV-0144</b><small>Mehta Studio</small></div>
      <span className={styles.tagDue}>Overdue</span>
    </div>
    <div className={styles.phoneLock}>🔐 Protected by PIN &amp; biometrics</div>
  </div>
);


/* ─── Contact form (WhatsApp / Email) ─── */
const WHATSAPP_NUMBER = '919804619202'; // +91 9804619202
const CONTACT_EMAIL = 'connect@beta-edge.com';

const ContactForm = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const buildText = () =>
    `Hello BETA Edge team, I'm interested in LedgerLock.\n\n` +
    `Name: ${form.name.trim()}\n` +
    `Contact Number: ${form.phone.trim()}\n` +
    `Email: ${form.email.trim()}\n\n` +
    `Message: ${form.message.trim()}`;

  const validate = () => {
    if (!form.name.trim()) return 'Please enter your name.';
    if (form.phone.replace(/\D/g, '').length < 7) return 'Please enter a valid contact number.';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return 'Please enter a valid email address.';
    if (!form.message.trim()) return 'Please write a short message.';
    return '';
  };

  const sendWhatsApp = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildText())}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const sendEmail = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    const subject = `LedgerLock enquiry from ${form.name.trim()}`;
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildText())}`;
  };

  return (
    <form className={styles.form} onSubmit={sendWhatsApp} noValidate aria-label="Contact LedgerLock team">
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label htmlFor="ll-name">Name</label>
          <input id="ll-name" name="name" type="text" autoComplete="name" placeholder="Your full name" value={form.name} onChange={handleChange} />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="ll-phone">Contact Number</label>
          <input id="ll-phone" name="phone" type="tel" autoComplete="tel" placeholder="+91 98XXXXXXXX" value={form.phone} onChange={handleChange} />
        </div>
      </div>
      <div className={styles.formGroup}>
        <label htmlFor="ll-email">Email</label>
        <input id="ll-email" name="email" type="email" autoComplete="email" placeholder="you@company.com" value={form.email} onChange={handleChange} />
      </div>
      <div className={styles.formGroup}>
        <label htmlFor="ll-message">Your Message</label>
        <textarea id="ll-message" name="message" rows={4} placeholder="Tell us about your business and what you need…" value={form.message} onChange={handleChange} />
      </div>
      {error && <p className={styles.formError} role="alert">{error}</p>}
      <div className={styles.formActions}>
        <button type="submit" className={styles.waBtn}>
          <span aria-hidden="true">💬</span> Contact us via WhatsApp — quick reply
        </button>
        <span className={styles.formOr}>or</span>
        <button type="button" className={styles.mailBtn} onClick={sendEmail}>
          <span aria-hidden="true">✉️</span> Just email our team
        </button>
      </div>
      <p className={styles.formNote}>
        WhatsApp opens with your message pre-filled for +91 98046 19202. Prefer email? Write to{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </form>
  );
};

/* ─── Main Page ─── */
const LedgerLockPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const whyRef = useRef(null);
  const featuresRef = useRef(null);
  const compareRef = useRef(null);
  const plansRef = useRef(null);
  const contactRef = useRef(null);

  const scrollTo = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <div className={styles.page}>
      <Seo
        title="LedgerLock — Simple, Secure, Offline Accounting App | BETA Edge"
        description="LedgerLock is an offline-first accounting app for small businesses, freelancers and entrepreneurs. Invoices, expenses, inventory, GST/VAT and reports — with local data storage, PIN and biometric protection."
        keywords="offline accounting app, offline invoicing app, small business accounting, GST billing app, VAT accounting software, secure accounting app, bahi khata app, freelancer invoicing, inventory and billing app, LedgerLock"
        canonical="https://www.beta-edge.com/ledgerlock"
        ogTitle="LedgerLock — Simple. Secure. Offline."
        ogDescription="The offline-first accounting app that keeps your data on your device and your business running without internet."
        ogUrl="https://www.beta-edge.com/ledgerlock"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'LedgerLock',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Android, iOS',
            description:
              'Offline-first accounting app for invoices, customers, expenses, products, payments and taxes with local data storage, PIN and biometric protection.',
            url: 'https://www.beta-edge.com/ledgerlock',
            installUrl: PLAY_STORE_URL,
            downloadUrl: PLAY_STORE_URL,
            publisher: { '@type': 'Organization', name: 'BETA Edge', url: 'https://www.beta-edge.com/' },
            offers: [
              { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD' },
            ],
          },
        ]}
      />
      <ParticleNetwork
        dotColor="#10b981"
        lineColor="16, 185, 129"
        cursorLineColor="110, 231, 183"
      />

      {/* ── Nav ── */}
      <nav className={styles.nav} aria-label="LedgerLock navigation">
        <div className={styles.navBrand}>
          <Link to="/" className={styles.brandBack} title="Back to BETA Edge">← BETA Edge</Link>
          <span className={styles.brandDivider}>|</span>
          <span className={styles.brandName}>LedgerLock <span className={styles.brandTag}>App</span></span>
        </div>
        <button className={styles.burger} aria-label="Toggle menu" onClick={() => setMenuOpen((p) => !p)}>☰</button>
        <div className={`${styles.navMenu} ${menuOpen ? styles.navMenuOpen : ''}`}>
          <button onClick={() => scrollTo(whyRef)}>Why LedgerLock</button>
          <button onClick={() => scrollTo(featuresRef)}>Features</button>
          <button onClick={() => scrollTo(compareRef)}>Compare</button>
          <button onClick={() => scrollTo(plansRef)}>Plans</button>
          <button onClick={() => scrollTo(contactRef)}>Contact</button>
          <a className={styles.navCta} href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">Download</a>
        </div>
      </nav>

      {/* ── Hero ── */}
      <header className={styles.hero} role="banner">
        <div className={styles.heroBg} aria-hidden="true" />
        <div className={styles.heroInner}>
          <div className={styles.heroContent}>
            <p className={styles.heroEyebrow}>Offline-first accounting</p>
            <h1 className={styles.heroHeadline}>
              Simple. Secure.<br />
              <span className={styles.heroAccent}>Offline.</span>
            </h1>
            <p className={styles.heroSub}>
              LedgerLock is the accounting app for small businesses, freelancers, professionals and entrepreneurs.
              Manage sales, invoices, customers, expenses, products, payments and taxes — even without internet.
            </p>
            <p className={styles.heroQuote}>Your business shouldn&apos;t stop because your internet does.</p>
            <div className={styles.heroActions}>
              <a
                className={styles.downloadBtn}
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span aria-hidden="true">▶</span> Download and Experience LedgerLock
              </a>
              <button className={styles.heroSecondary} onClick={() => scrollTo(whyRef)}>See why it&apos;s better ↓</button>
              <button className={styles.heroSecondary} onClick={() => scrollTo(plansRef)}>Free &amp; Pro plans →</button>
            </div>
            <div className={styles.heroBadges}>
              {TRUST_CHIPS.map((c) => <span key={c}>✓ {c}</span>)}
            </div>
          </div>
          <div className={styles.heroVisual}>
            <PhoneMock />
          </div>
        </div>
      </header>

      {/* ── Why LedgerLock is better ── */}
      <section className={styles.section} ref={whyRef} id="why" aria-label="Why LedgerLock is better">
        <div className={styles.sectionInner}>
          <p className={styles.sectionEyebrow}>Why LedgerLock is better</p>
          <h2 className={styles.sectionTitle}>Built differently from the apps you&apos;ve outgrown</h2>
          <p className={styles.sectionSub}>
            Most accounting software assumes you&apos;re always online, always logged in and always paying.
            LedgerLock starts from how small businesses really work.
          </p>
          <div className={styles.whyGrid}>
            {WHY_BETTER.map((w) => (
              <div key={w.num} className={styles.whyCard}>
                <span className={styles.whyNum}>{w.num}</span>
                <h3>{w.title}</h3>
                <p>{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Comparison ── */}
      <section className={styles.section} ref={compareRef} id="compare" aria-label="LedgerLock comparison">
        <div className={styles.sectionInner}>
          <p className={styles.sectionEyebrow}>Head to head</p>
          <h2 className={styles.sectionTitle}>LedgerLock vs. the usual alternatives</h2>
          <p className={styles.sectionSub}>
            A general comparison against typical cloud-only accounting apps and spreadsheets or paper ledgers.
            Individual products vary.
          </p>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Capability</th>
                  <th scope="col" className={styles.thHero}>LedgerLock</th>
                  <th scope="col">Typical cloud-only apps</th>
                  <th scope="col">Spreadsheets &amp; paper</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((r) => (
                  <tr key={r.feature}>
                    <th scope="row">{r.feature}</th>
                    <td className={styles.tdHero}><Cell value={r.ledger} /></td>
                    <td><Cell value={r.cloud} /></td>
                    <td><Cell value={r.sheet} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={styles.legend}>
            <span className={styles.yes}>✓</span> Yes &nbsp;
            <span className={styles.partial}>~</span> Varies by product or plan &nbsp;
            <span className={styles.no}>✕</span> Generally not
          </p>
        </div>
      </section>

      {/* ── Privacy ── */}
      <section className={styles.section} aria-label="Privacy and security">
        <div className={styles.sectionInner}>
          <p className={styles.sectionEyebrow}>🔐 Your data, your business</p>
          <h2 className={styles.sectionTitle}>Privacy-first, by default</h2>
          <p className={styles.sectionSub}>
            Your accounting data is stored locally on your device and protected with industry-standard security technologies.
          </p>
          <div className={styles.privacyGrid}>
            {PRIVACY.map((p) => (
              <div key={p.title} className={styles.privacyCard}>
                <span className={styles.privacyIcon}>{p.icon}</span>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </div>
            ))}
          </div>
          <p className={styles.note}>Paid plans can additionally provide secure cloud storage and synchronization.</p>
        </div>
      </section>

      {/* ── Features ── */}
      <section className={styles.section} ref={featuresRef} id="features" aria-label="Features">
        <div className={styles.sectionInner}>
          <p className={styles.sectionEyebrow}>📊 Complete business accounting</p>
          <h2 className={styles.sectionTitle}>Everything for everyday accounting, in one simple app</h2>
          <div className={styles.moduleGrid}>
            {MODULES.map((m) => (
              <article key={m.title} className={styles.moduleCard}>
                <div className={styles.moduleIcon}>{m.icon}</div>
                <h3>{m.title}</h3>
                <ul>
                  {m.items.map((i) => <li key={i}>{i}</li>)}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Taxes & Reports ── */}
      <section className={styles.section} aria-label="Taxes and reports">
        <div className={styles.sectionInner}>
          <div className={styles.twoCol}>
            <div className={styles.panel}>
              <p className={styles.sectionEyebrow}>🌎 GST, VAT &amp; taxes</p>
              <h3 className={styles.panelTitle}>Designed for businesses around the world</h3>
              <p className={styles.panelText}>Support for configurable tax setups:</p>
              <div className={styles.chips}>
                {TAXES.map((t) => <span key={t}>{t}</span>)}
              </div>
              <p className={styles.panelNoteText}>
                Country-specific tax capabilities can be expanded as LedgerLock grows into additional markets.
              </p>
            </div>
            <div className={styles.panel}>
              <p className={styles.sectionEyebrow}>📈 Business reports</p>
              <h3 className={styles.panelTitle}>A clear view of your business</h3>
              <p className={styles.panelText}>Useful reports, ready when you are:</p>
              <div className={styles.chips}>
                {REPORTS.map((r) => <span key={r}>{r}</span>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Offline ── */}
      <section className={styles.offline} aria-label="Works offline">
        <div className={styles.sectionInner}>
          <p className={styles.sectionEyebrow}>📱 Works offline</p>
          <h2 className={styles.sectionTitle}>Offline-first by design</h2>
          <p className={styles.sectionSub}>
            Create invoices, record expenses, manage customers, review transactions and keep running your business without an
            active internet connection. When online features are available for your plan, your data can synchronize with the cloud.
          </p>
          <div className={styles.flow} aria-label="Business workflow">
            {FLOW.map((f, i) => (
              <React.Fragment key={f}>
                <span className={styles.flowStep}>{f}</span>
                {i < FLOW.length - 1 && <span className={styles.flowArrow} aria-hidden="true">→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── Plans ── */}
      <section className={styles.section} ref={plansRef} id="plans" aria-label="Free and Pro plans">
        <div className={styles.sectionInner}>
          <p className={styles.sectionEyebrow}>☁️ Free &amp; Pro</p>
          <h2 className={styles.sectionTitle}>Start free. Upgrade when you need the cloud.</h2>
          <div className={styles.plans}>
            <article className={styles.plan}>
              <h3>Free</h3>
              <p className={styles.planSub}>Essential accounting tools</p>
              <ul>{FREE_FEATURES.map((f) => <li key={f}>{f}</li>)}</ul>
            </article>
            <article className={`${styles.plan} ${styles.planPro}`}>
              <span className={styles.planBadge}>14-day Pro trial for new users*</span>
              <h3>Pro 🚀</h3>
              <p className={styles.planSub}>Everything in Free, plus:</p>
              <ul>{PRO_FEATURES.map((f) => <li key={f}>{f}</li>)}</ul>
            </article>
          </div>
          <p className={styles.note}>
            *New users may receive a 14-day Pro trial. Your local accounting data remains accessible even if your Pro trial or
            subscription ends. Cloud features require an eligible Pro plan.
          </p>
        </div>
      </section>

      {/* ── Account + Audience ── */}
      <section className={styles.section} aria-label="Account access and audience">
        <div className={styles.sectionInner}>
          <div className={styles.twoCol}>
            <div className={styles.panel}>
              <p className={styles.sectionEyebrow}>🔑 Secure account access</p>
              <h3 className={styles.panelTitle}>Sign in your way</h3>
              <div className={styles.chips}>
                <span>Google Sign-In</span>
                <span>Email &amp; Password</span>
              </div>
              <p className={styles.panelNoteText}>Account authentication is securely managed using Firebase Authentication.</p>
            </div>
            <div className={styles.panel}>
              <p className={styles.sectionEyebrow}>🚀 Simple by design</p>
              <h3 className={styles.panelTitle}>Made for people who run businesses</h3>
              <div className={styles.chips}>
                {AUDIENCE.map((a) => <span key={a}>{a}</span>)}
              </div>
              <p className={styles.panelNoteText}>
                Keep your finances organized without unnecessary complexity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Download band ── */}
      <section className={styles.cta} aria-label="Download LedgerLock">
        <div className={styles.ctaInner}>
          <h2>Ready to run your business offline?</h2>
          <p>Get LedgerLock on Google Play and start free — no internet required.</p>
          <a className={styles.downloadBtn} href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
            <span aria-hidden="true">▶</span> Download and Experience LedgerLock
          </a>
        </div>
      </section>

      {/* ── Contact ── */}
      <section className={styles.contact} ref={contactRef} id="contact" aria-label="Contact the LedgerLock team">
        <div className={styles.contactInner}>
          <div className={styles.contactIntro}>
            <p className={styles.sectionEyebrow}>Get in touch</p>
            <h2 className={styles.sectionTitle}>Simple. Secure. Offline.<br />Let&apos;s talk.</h2>
            <p className={styles.sectionSub}>
              Questions about LedgerLock, plans or your business setup? Message us on WhatsApp for the quickest reply,
              or email our team — whichever suits you.
            </p>
            <ul className={styles.contactList}>
              <li><span>💬</span><div><strong>WhatsApp (fastest)</strong><a href="https://wa.me/919804619202" target="_blank" rel="noopener noreferrer">+91 98046 19202</a></div></li>
              <li><span>✉️</span><div><strong>Email</strong><a href="mailto:connect@beta-edge.com">connect@beta-edge.com</a></div></li>
            </ul>
          </div>
          <div className={styles.contactCard}>
            <ContactForm />
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className={styles.footer} role="contentinfo">
        <p>
          © {new Date().getFullYear()} <strong>LedgerLock</strong> — Powered by BETA Edge
          &nbsp;|&nbsp; <Link to="/">Back to BETA Edge →</Link>
        </p>
      </footer>
    </div>
  );
};

export default LedgerLockPage;
